import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import { ProductDataType, ProductType } from '@pages/product-analyzer/Product';
import { Country, FeatureOption, FilterKeys, Filters, FilterTitle, Option } from '@utils/pageConfig/Types';
import { KpiPercentageResp, KpiResp } from './CommonResp';
import { RelativePeriod } from '@components/comparison-period-selector/Types';
import { BaseTimePeriod } from '@services/FiltersTypes';
import { CollectionLiveData } from '@components/collections/Model';

export interface BrandDependencyReqParam extends CollectionLiveData {
  clientId: number;
  countries: Country[];
  periodType: Periodicity;
  period: string;
  comparisonPeriod: RelativePeriod | BaseTimePeriod;
  productType: ProductType;
  productDataType: ProductDataType;
  appliedFilters: Filters[];
  productGroup: Option[];
  segmentByDataType?: string;
  segmentBy?: string;
}

export interface BrandDependencyChartSubResp {
  brand?: string;
  featureValue?: string;
  marketRevenue: KpiResp;
  marketRevenueShare: KpiPercentageResp;
  retailerRevenue: KpiResp;
  retailerRevenueShare: KpiPercentageResp;
  retailerWithinBrandRevenue: KpiPercentageResp;
  brandWithinRetailerRevenue: KpiPercentageResp;
  marketUnits: KpiResp;
  marketUnitsShare: KpiPercentageResp;
  retailerUnits: KpiResp;
  retailerUnitsShare: KpiPercentageResp;
  retailerWithinBrandUnits: KpiPercentageResp;
  brandWithinRetailerUnits: KpiPercentageResp;
}

interface BrandDependencyChartSubData {
  revenue: KpiPercentageResp;
  units: KpiPercentageResp;
}

export enum BrandDependencyCategory {
  revenue = 'revenue',
  units = 'units',
}

export type BrandDependencyChartDependencyData = Record<keyof typeof BrandDependencyCategory, number>;

export enum BrandDependencyFocusArea {
  market = 'market',
  retailer = 'retailer',
}

export interface BrandDependencyChartData {
  brand?: string;
  featureValue?: string;
  marketRevenue: KpiResp;
  marketRevenueShare: KpiPercentageResp;
  marketUnits: KpiResp;
  marketUnitsShare: KpiPercentageResp;
  retailerRevenue: KpiResp;
  retailerRevenueShare: KpiPercentageResp;
  retailerUnits: KpiResp;
  retailerUnitsShare: KpiPercentageResp;
  retailerWithinBrand: BrandDependencyChartSubData;
  brandWithinRetailer: BrandDependencyChartSubData;
}

export enum BrandDependencyKpi {
  marketRevenue = 'marketRevenue',
  marketRevenueShare = 'marketRevenueShare',
  marketUnits = 'marketUnits',
  marketUnitsShare = 'marketUnitsShare',
  retailerRevenue = 'retailerRevenue',
  retailerRevenueShare = 'retailerRevenueShare',
  retailerUnits = 'retailerUnits',
  retailerUnitsShare = 'retailerUnitsShare',
}

export const BrandDependencyKpiDisplayName = {
  [BrandDependencyKpi.marketRevenue]: 'Brand Revenue',
  [BrandDependencyKpi.marketRevenueShare]: 'Brand Revenue Share',
  [BrandDependencyKpi.marketUnits]: 'Brand Units',
  [BrandDependencyKpi.marketUnitsShare]: 'Brand Units Share',
  [BrandDependencyKpi.retailerRevenue]: 'Retailer Revenue',
  [BrandDependencyKpi.retailerRevenueShare]: 'Retailer Revenue Share',
  [BrandDependencyKpi.retailerUnits]: 'Retailer Units',
  [BrandDependencyKpi.retailerUnitsShare]: 'Retailer Units Share',
};

export enum BrandDependencySort {
  retailerShareOfBrand = 'retailerShareOfBrand',
  brandShareOfRetailer = 'brandShareOfRetailer',
  alphabeticallyAscending = 'alphabeticallyAscending',
  alphabeticallyDescending = 'alphabeticallyDescending',
}

export const BrandDependencySortDisplayName = {
  [BrandDependencySort.retailerShareOfBrand]: 'Retailer Share of Brand',
  [BrandDependencySort.brandShareOfRetailer]: 'Brand Share of Retailer',
  [BrandDependencySort.alphabeticallyAscending]: 'Alphabetically (Ascending)',
  [BrandDependencySort.alphabeticallyDescending]: 'Alphabetically (Descending)',
};

export const BrandDependencySortToKpiMapping: Record<
  BrandDependencySort,
  'retailerWithinBrand' | 'brandWithinRetailer' | 'alphabeticallyAscending' | 'alphabeticallyDescending'
> = {
  [BrandDependencySort.retailerShareOfBrand]: 'retailerWithinBrand',
  [BrandDependencySort.brandShareOfRetailer]: 'brandWithinRetailer',
  [BrandDependencySort.alphabeticallyAscending]: 'alphabeticallyAscending',
  [BrandDependencySort.alphabeticallyDescending]: 'alphabeticallyDescending',
};

export const isKpiResp = (kpi: KpiResp | KpiPercentageResp): kpi is KpiResp =>
  'value' in kpi && 'percentage' in kpi && 'actual' in kpi;

export type BrandDependencyChartResp = Record<
  BrandDependencyKpi | 'selectedBrandsData',
  BrandDependencyChartSubResp[] | null
>;

export enum ComponentName {
  RETAILER_MARKET_RELATIONSHIP_BUTTERFLY_CHART = 'Retailer and market relationship Butterfly Chart',
}

export const INITIAL_BRAND_FILTER: Filters = {
  dataType: 'Brand',
  feature: 'Brand',
  filterKey: FilterKeys.BRAND,
  filterTitle: 'Brand',
  options: [],
  disabled: false,
};

export const buildOptions = (filters: Filters[]): FeatureOption[] => {
  return filters
    .filter((f) => f.feature !== null)
    .map((f) => {
      return {
        dataType: f.dataType,
        feature: f.feature,
        value: f.filterTitle,
        label: f.filterTitle,
        filterKey: f.filterKey,
      };
    });
};

export const INITIAL_BY_SEGMENT_OPTION: FeatureOption = buildOptions([
  {
    dataType: FilterTitle.SELECT,
    feature: FilterTitle.SELECT,
    filterKey: FilterKeys.SELECT,
    filterTitle: FilterTitle.SELECT,
    options: [],
    disabled: false,
  },
])[0];

export type BrandDependencyApiEndpoint = 'brand-dependency-chart' | 'brand-deep-dive';

export type BrandDeepDiveChartRespDataLookup = {
  brand: string;
  data: BrandDependencyChartSubResp[];
} & BrandDependencyChartSubResp;

export type BrandDeepDiveChartResp = {
  dataLookup: BrandDeepDiveChartRespDataLookup[];
} & Record<BrandDependencyKpi, string[]>;
